import classNames from "classnames";
import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./dropDown.scss";
import { Spinner } from "@material-tailwind/react";
import SvgIcon from "@/components/common/SvgIcon";
import { Typography } from "@/components/kit";

export type TOptions<T = any> =
  | string
  | number
  | ({
      disabled?: boolean;
      [keys: string]: any;
    } & T);

export type TSelectBoxProps<T = any> = {
  options: TOptions<T>[];
  name?: string;
  onChange: (value: any, data: T) => void;
  onClose?: () => void;
  value: any;
  label?: string;
  className?: string;
  placeholder?: ReactNode;
  placeholderIcon?: string;
  error?: string | boolean;
  valueKey?: string;
  optionKey?: string;
  renderValue?: (data: T) => ReactNode | undefined;
  renderOption?: (data: T) => ReactNode | undefined;
  disabled?: boolean;
  readonly?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  loading?: boolean;
  startAdornment?: ReactElement | string;
  arrowClassName?: string;
  ulClassName?: string;
  clearable?: boolean;
  containerClassName?: string;
  isRequired?: boolean;
  listClassName?: string;
  uiListClassName?: string;
  isOpentoTop?: boolean;
  maxDropdownHeight?: number; // New prop for customizable max height
  fixedDropdown?: boolean; // Render dropdown with position: fixed to overlay tables
  dropdownZIndex?: number; // Custom z-index when fixed
};

const SelectBox = <T = any,>(props: TSelectBoxProps<T>) => {
  const {
    options = [],
    arrowClassName = "",
    ulClassName = "",
    name,
    label,
    value,
    onChange,
    onClose = () => null,
    onFocus = () => null,
    error,
    valueKey = "value",
    optionKey = "title",
    renderValue = () => null,
    renderOption = () => null,
    className,
    disabled = false,
    readonly = false,
    loading = false,
    startAdornment,
    clearable = false,
    isRequired = false,
    containerClassName = "",
    placeholder,
    placeholderIcon,
    listClassName,
    uiListClassName,
    isOpentoTop,
    maxDropdownHeight = 225,
  } = props;

  const [isFocus, setIsFocus] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: string;
    bottom?: string;
    maxHeight: string;
  }>({ maxHeight: `${maxDropdownHeight}px` });
  const [fixedCoords, setFixedCoords] = useState<{
    top?: number;
    left?: number;
    width?: number;
    maxHeight?: number;
  }>({});

  const ref = useRef<any>(null);
  const ulRef = useRef<any>(null);
  const containerRef = useRef<any>(null);
  const localOptions = options.slice();

  // Calculate dropdown position when it opens
  const calculateDropdownPosition = useCallback(() => {
    if (!ref.current || !containerRef.current) return;

    const selectRect = ref.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Calculate available space above and below
    const spaceBelow = viewportHeight - selectRect.bottom;
    const spaceAbove = selectRect.top;

    // Calculate ideal dropdown height
    const itemHeight = 35; // Height per option
    const idealHeight = Math.min(
      options.length * itemHeight,
      maxDropdownHeight,
    );

    // Determine if we should open upward
    const shouldOpenUp =
      isOpentoTop || (spaceBelow < idealHeight && spaceAbove > spaceBelow);

    if (shouldOpenUp) {
      // Open upward
      const availableHeight = Math.min(idealHeight, spaceAbove - 10); // 10px buffer
      setDropdownPosition({
        bottom: `${selectRect.height + 4}px`,
        maxHeight: `${availableHeight}px`,
      });
      setFixedCoords({
        top: Math.max(10, selectRect.top - availableHeight - 4),
        left: selectRect.left,
        width: selectRect.width,
        maxHeight: availableHeight,
      });
    } else {
      // Open downward (default)
      const availableHeight = Math.min(idealHeight, spaceBelow - 10); // 10px buffer
      setDropdownPosition({
        top: `${selectRect.height + 4}px`,
        maxHeight: `${availableHeight}px`,
      });
      setFixedCoords({
        top: Math.min(viewportHeight - 10, selectRect.bottom + 4),
        left: selectRect.left,
        width: selectRect.width,
        maxHeight: availableHeight,
      });
    }
  }, [options.length, maxDropdownHeight, isOpentoTop]);

  const optionClick = (option: any) => {
    if (option.disabled || readonly) return;

    setIsFocus(false);
    onChange(getItemValue(option), option);
  };

  const activeFocus = (e: any) => {
    if (disabled || readonly) return;
    if (isFocus) return setIsFocus(false);
    onFocus(e);
    ref.current.focus();
    setIsFocus(true);
  };

  // Recalculate position when dropdown opens
  useEffect(() => {
    if (isFocus) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(calculateDropdownPosition, 0);
      return () => clearTimeout(timer);
    }
  }, [isFocus, calculateDropdownPosition]);

  // Recalculate on window resize
  useEffect(() => {
    if (!isFocus) return;

    const handleResize = () => calculateDropdownPosition();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isFocus, calculateDropdownPosition]);

  useEffect(() => {
    const clickHandler = (e: any) => {
      if (!ref.current?.contains(e.target)) {
        setIsFocus(false);
      }
    };
    document.body.addEventListener("click", clickHandler, true);
    return () => document.body.removeEventListener("click", clickHandler, true);
  }, []);

  const getItemValue = useCallback(
    (option: TOptions) => {
      if (typeof option !== "object") return option;
      if (option.hasOwnProperty(valueKey)) return option[valueKey];
      return JSON.stringify(option);
    },
    [valueKey],
  );

  const getItemText = useCallback(
    (option: any) => {
      if (typeof option !== "object") return option;
      if (option.hasOwnProperty(optionKey)) return option[optionKey];
      return option.toString();
    },
    [optionKey],
  );

  const selected = useMemo(
    () => localOptions.find((option) => getItemValue(option) === value),
    [value, localOptions],
  );
  const isActive = isFocus || value || value === 0;

  useEffect(() => {
    if (isFocus) return;
    onClose();
  }, [isFocus]);

  return (
    <div className={containerClassName} ref={containerRef}>
      <div
        ref={ref}
        className={classNames(`customDropDown select-none relative`, {
          err: !!error,
          active: isActive,
        })}
      >
        <button
          type="button"
          value={value}
          name={name}
          className={classNames(
            `box disabled:border-gray-200 disabled:shadow-none disabled:opacity-30 disabled:cursor-default disabled:bg-gray-100 px-4 flex-shrink-0 ${
              className ?? ""
            }`,
            {
              "cursor-default": readonly,
            },
          )}
          disabled={disabled}
          onClick={activeFocus}
        >
          {!!startAdornment && (
            <div className="flex justify-center items-center py-2.5 mr-2">
              {startAdornment}
            </div>
          )}
          <div
            className={classNames(
              "h-full flex items-center min-w-0 overflow-hidden",
              {
                "py-2.5": !!label,
              },
            )}
          >
            {!(selected || value) && placeholder && (
              <div className="flex gap-2 items-center">
                {placeholderIcon && <SvgIcon iconName={placeholderIcon} />}

                <Typography variant="Body1-Regular" color="text-gray-400">
                  {placeholder}
                </Typography>
              </div>
            )}
            {!!label && (
              <label
                className={classNames("cursor-default  !text-gray-200", {
                  "opacity-30 text-gray-200": disabled,
                  "cursor-pointer": !disabled && !readonly,
                })}
                onClick={activeFocus}
                htmlFor={name}
              >
                {label}
                {isRequired && <span className={"ml-0.5 text-[16px]"}>*</span>}
              </label>
            )}

            {(selected || value) && (
              <div
                className={classNames(
                  "text-start text-gray-900 min-w-0 overflow-hidden",
                  {
                    "mt-3": !!label,
                  },
                )}
              >
                {(selected &&
                  (renderValue(selected as T) ||
                    (typeof selected === "object"
                      ? selected[optionKey]
                      : selected))) ||
                  renderValue(value) ||
                  value}
              </div>
            )}
          </div>
          <div className="flex-1" />
          {(loading && (
            <div>
              <Spinner className="h-4 w-4" />
            </div>
          )) || (
            <>
              {clearable && !!value && (
                <SvgIcon
                  fill="fill-gray-800"
                  className={
                    "cursor-pointer  dark:!fill-white icon w-6 h-6 mr-2"
                  }
                  iconName="close"
                />
              )}
              {!readonly && (
                <div className={"py-2.5 h-full flex flex-shrink-0"}>
                  <div
                    className={classNames(
                      "border-gray-200 pl-2 flex justify-center items-center",
                      arrowClassName,
                    )}
                  >
                    <SvgIcon
                      iconName="arrowDown"
                      fill="fill-gray-800"
                      className={classNames(
                        "fill-gray-800 icon w-[10px] h-[10px] dark:!fill-white  transition-all",
                        {
                          "rotate-180": isFocus,
                        },
                      )}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </button>

        {isFocus && !props.fixedDropdown && (
          <div
            className={classNames(
              `${ulClassName} absolute min-w-full px-0 z-50 bg-white divide-y divide-gray-100 rounded-md shadow`,
            )}
            style={{
              ...dropdownPosition,
            }}
          >
            <ul
              ref={ulRef}
              className={classNames("overflow-y-auto", uiListClassName)}
              style={{ maxHeight: dropdownPosition.maxHeight }}
            >
              {localOptions.map((option) => (
                <li
                  onClick={() => optionClick(option)}
                  key={getItemValue(option)}
                  className={`${classNames(listClassName)} ${
                    typeof option === "object" && option.disabled
                      ? "opacity-40"
                      : ""
                  }`}
                >
                  {renderOption(option as T) || getItemText(option)}
                </li>
              ))}
            </ul>
          </div>
        )}
        {isFocus && props.fixedDropdown && (
          <div
            className={classNames(
              `${ulClassName} fixed px-0 bg-white divide-y divide-gray-100 rounded-md shadow`,
            )}
            style={{
              top: fixedCoords.top,
              left: fixedCoords.left,
              width: fixedCoords.width,
              zIndex: props.dropdownZIndex ?? 9999,
            }}
          >
            <ul
              ref={ulRef}
              className={classNames("overflow-y-auto", uiListClassName)}
              style={{ maxHeight: fixedCoords.maxHeight }}
            >
              {localOptions.map((option) => (
                <li
                  onClick={() => optionClick(option)}
                  key={getItemValue(option)}
                  className={`${classNames(listClassName)} ${
                    typeof option === "object" && option.disabled
                      ? "opacity-40"
                      : ""
                  }`}
                >
                  {renderOption(option as T) || getItemText(option)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {!!error && (
        <div className="error ps-2 pt-2 flex justify-start items-center gap-1">
          <SvgIcon
            className={"w-3 h-3"}
            fill="fill-red-600"
            iconName="attentionRed"
          />
          <Typography color={"text-red-600"} variant={"Caption1-Regular"}>
            {error}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default SelectBox;
